import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  VStack
} from '@chakra-ui/react';
import Budget from './Budget';
import StyleSelector from './StyleSelector';
import PreferencesHeader from './PreferencesHeader';
import PreferencesSubmit from './PreferencesSubmit';
import BudgetHeader from './BudgetHeader';
import IntentSelector from './IntentSelector';

const Preferences = ({ 
  onBudgetSet, 
  currentBudget = null, 
  currentStyle = 'women', 
  isVisible = true,
  showBudgetInput = true,
  onEdit
}) => {
  const [budget, setBudget] = useState(currentBudget || '');
  const [stylePreference, setStylePreference] = useState(currentStyle);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentBudget) {
      setBudget(currentBudget);
    }
    setStylePreference(currentStyle);
    
    // Load saved intent from localStorage
    const savedIntent = localStorage.getItem('userIntent');
    if (savedIntent) {
      setSelectedIntent(savedIntent);
    }
  }, [currentBudget, currentStyle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const budgetValue = parseInt(budget);
    
    if (budgetValue && budgetValue > 0 && selectedIntent) {
      setIsLoading(true);
      
      // Simulate brief loading for smooth UX
      setTimeout(() => {
        onBudgetSet(budgetValue, stylePreference, selectedIntent);
        setIsLoading(false);
      }, 300);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  if (!isVisible) return null;

  return (
    <Box className="bg-pink-950" minH={showBudgetInput ? "20vh" : "auto"}>
      <VStack spacing={0} align="stretch" maxW="4xl" mx="auto">
        
        {/* Budget Input Section - Shows initially or when editing */}
        <AnimatePresence mode="wait">
          {showBudgetInput && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full"
            >
              <Box 
                className="bg-gradient-to-br from-pink-50 to-rose-50 backdrop-blur-sm rounded-3xl shadow-2xl mx-4 md:mx-8 overflow-hidden border border-orange-100/30"
                minH="auto"
              >
                <VStack 
                  spacing={8} 
                  p={{ base: 8, md: 12 }} 
                  justify="center" 
                  align="center"
                  minH="auto"
                  className="bg-gradient-to-br from-orange-50/50 to-orange-100/30"
                >
                  <PreferencesHeader />

                  <motion.form
                    onSubmit={handleSubmit}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="w-full max-w-sm"
                  >
                    <VStack spacing={10}>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.3 }}
                        className="w-full"
                      >
                        <StyleSelector
                          selectedStyle={stylePreference}
                          onStyleChange={setStylePreference}
                          className="mb-4"
                        />
                      </motion.div>
                      
                      <Budget
                        budget={budget}
                        onBudgetChange={setBudget}
                        onKeyPress={handleKeyPress}
                      />

                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.3 }}
                        className="w-full mt-5"
                      >
                        <IntentSelector
                          selectedIntent={selectedIntent}
                          onIntentChange={setSelectedIntent}
                        />
                      </motion.div>

                      <Box mt={3}>
                        <PreferencesSubmit
                          isLoading={isLoading}
                          isDisabled={!budget || parseInt(budget) <= 0 || !selectedIntent}
                          onSubmit={handleSubmit}
                        />
                      </Box>
                    </VStack>
                  </motion.form>
                </VStack>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Budget Header - Shows after budget is set */}
        <AnimatePresence mode="wait">
          {!showBudgetInput && budget && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full"
            >
              <Box className="bg-gradient-to-br from-amber-50 to-orange-50 backdrop-blur-sm rounded-3xl shadow-2xl mx-4 md:mx-8 overflow-hidden border border-orange-100/30">
                <BudgetHeader 
                  budget={budget}
                  style={stylePreference}
                  onEdit={onEdit} 
                  isVisible={!showBudgetInput && budget} 
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
        
      </VStack>
    </Box>
  );
};

export default Preferences;